import React,{ useRef, useState, useEffect, useCallback } from "react";
import {Box, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {makeStyles} from "@mui/styles";


const useStyles = makeStyles((theme)=>{
    return {
        nativeVideoPlayerWrapper:{
            position: 'relative',
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '@supports not(aspect-ratio)':{
                position: 'absolute',
                top: 0,
                height: '100%'
            }
        }
    }
})


const buildVTEvent = (video, sessionId, player) => {
    const { currentTime, duration } = player;
    return {
        pg: video.parentId,
        vd: video.id,
        li: video.loginId,
        vvs: sessionId,
        vt: currentTime,
        vct: currentTime,
        vdr: duration
    };
}
const buildVTPEvent = (video, sessionId, player, percentage) => {
    const { currentTime, duration } = player;
    const vtpObject = {
        vd: video.id,
        li: video.loginId,
        vt: currentTime,
        vvs: sessionId,
        vct: currentTime,
        vdr: duration,
        vtp: percentage
    }
    return vtpObject;
}

const getVideoSource = (asset) =>{
    if(asset.sources?.length > 0){
        const sources = [...asset.sources]
        let sortedSource = sources.sort((a, b)=>{
            if(a.width < b.width){
                return 1
            }else if(a.width > b.width){
                return -1
            }
            return 0
        });
        return sortedSource[1];
    }
    return null

}

const NativePlayer = (props) => {
    const playerRef = useRef(null);
    const { video, autoplay, sessionId, handleCloseModal, isLandscape, active, index, activePosition } = props;
    const [currentlyPlaying, setCurrentlyPlaying] = useState(video.id);
    const [initialized, setInitialized] = useState(false);
    const [vvEventTriggered, setVvEventTriggered] = useState(false);
    const [muted, setMuted] = useState(false);

    useEffect(() => {
        if (currentlyPlaying !== video.id) {
            setCurrentlyPlaying(video.id);
            setVvEventTriggered(false);
            const current = playerRef.current;
            if (current) {
                clearGlobalSessionId();
                current.load();
            }
        }
    }, [video, sessionId]);

    useEffect(() => {
        if(!playerRef?.current){
            return;
        }
        const handlePlayMute = () =>{
            if(playerRef?.current){
                playerRef.current.muted = true;
                setMuted(true);
                playerRef?.current?.play();
            }
        }
        if(active && autoplay && playerRef){
            playerRef?.current?.play().catch(()=>{
                handlePlayMute();
            });
        }else if(playerRef){
            playerRef?.current?.pause();
            // @ts-ignore
            playerRef.current.currentTime = 0;
        }
    }, [active, autoplay, playerRef]);

    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.muted = muted;
        }
    }, [muted]);

    useEffect(() => {
        const toTheLeft = activePosition - 3;
        const toTheRight = activePosition + 3;
        const shouldLoad = index >= toTheLeft && index <= toTheRight;
        if (shouldLoad) {
            setInitialized(true);
        }else{
            setInitialized(false);
        }
    }, [index, activePosition])


    const clearGlobalSessionId = useCallback(() => {
        window[sessionId] = null;
    }, [sessionId])

    const timeElapsed = useCallback(() => {

        if (playerRef.current) {
            const { currentTime, duration } = playerRef.current;
            const percentage = Math.floor((currentTime * 100) / duration);
            if (!vvEventTriggered) {
                setVvEventTriggered(true);
                const analyticsObj = {
                    pg: video.parentId,
                    vd: video.id,
                    li: video.loginId,
                    vvs: sessionId
                };
                window?._tvpa?.push(['track', 'vv', analyticsObj]);
            }
            const eventRegistered = window[sessionId];
            if (!eventRegistered) {
                window[sessionId] = {};
            }

            if ((Math.floor(currentTime) % 3) === 0 && Math.floor(currentTime) > 0) {
                const timeEvaluate = Math.floor(currentTime);
                if (!window[sessionId][timeEvaluate]) {
                    window[sessionId][timeEvaluate] = true;
                    window?._tvpa?.push(['track', 'vt', buildVTEvent(video, sessionId, playerRef.current)]);
                }
            }
            const generatePercentageEvent = (value, player) => {
                if (!window[sessionId][value]) {
                    window[sessionId][value] = true;
                    window?._tvpa?.push(['track', 'vtp', buildVTPEvent(video, sessionId, player, value)]);
                }
            }
            if (percentage >= 25 && percentage < 50) {
                generatePercentageEvent(25, playerRef.current);
            }
            if (percentage >= 50 && percentage < 75) {
                generatePercentageEvent(50, playerRef.current);
            }
            if (percentage >= 75 && percentage < 97) {
                generatePercentageEvent(75, playerRef.current);
            }
            if (percentage >= 97 && percentage <= 100) {
                generatePercentageEvent(100, playerRef.current);
            }
        }
    }, [video, vvEventTriggered, setVvEventTriggered, sessionId])

    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        // Define the event listeners outside to keep reference for cleanup
        const handleTimeUpdate = timeElapsed;

        const handleEnded = () => {
            setVvEventTriggered(false);
            player?.play();
            clearGlobalSessionId();
        };

        const handleFullscreenChange = () => {
            const isFullscreenNow = document?.fullscreenElement !== null;
            if (player) {
                // @ts-ignore
                player.controls = isFullscreenNow;
            }
        };

        const cleanupCallback = () => {
            clearGlobalSessionId();
            if (player) {
                player.removeEventListener("timeupdate", handleTimeUpdate);
                player.removeEventListener("ended", handleEnded);
                player.removeEventListener(
                    "mozfullscreenchange",
                    handleFullscreenChange
                );
            }
        };

        if (initialized) {
            player.addEventListener("timeupdate", handleTimeUpdate);
            player.addEventListener("ended", handleEnded);
            player.addEventListener(
                "mozfullscreenchange",
                handleFullscreenChange
            );
        }

        return () => {
            cleanupCallback();
        };
    }, [playerRef, initialized, timeElapsed, clearGlobalSessionId]);    

    const classes = useStyles();

    const playPauseHandler = () =>{
        const isFullscreenNow = document?.fullscreenElement !== null;
        if(isFullscreenNow){
            return;
        }
        if(playerRef?.current){
            if(playerRef.current.paused){
                playerRef.current.play();
            }else{
                playerRef.current.pause();
            }
        }
    }
    const productsEnabledValue = true;
    const controlsWidth = productsEnabledValue ? '100%' : '100%';

    const handleFullscreen = (e) =>{
        e.preventDefault();
        e.stopPropagation();
        const elem = playerRef?.current;
        // @ts-ignore
        if(elem?.webkitEnterFullScreen){
            // @ts-ignore
            elem?.webkitEnterFullScreen()
        }
        else if(elem?.requestFullscreen) {
            elem?.requestFullscreen();
        }
    }
    const source = getVideoSource(video.asset);
    return <div id={"creatable-video-wrapper"} style={isLandscape ?{height: '100%', display:'flex'} : {}} onClick={playPauseHandler} className={classes.nativeVideoPlayerWrapper}>
            {source && (
                <video
                    ref={playerRef}
                    width="100%"
                    height={"auto"}
                    playsInline
                    preload={"metadata"}
                >
                    <source src={source.file} type={source.mime}></source>
                </video>
            )}
        <Box width={{xs:'100%', sm: controlsWidth}} display={'flex'} position={'absolute'} top={"15px"} px={2} justifyContent={'space-between'}
        >
            <Box display={'flex'} zIndex={'1000'}>
                <IconButton sx={{'&:hover':{backgroundColor: 'inherit'}, cursor: 'pointer', display: { xs: 'flex', sm: 'none'}, width: 'auto'}} onClick={handleCloseModal} disableRipple>
                    <CloseIcon htmlColor={'#ffffff'} sx={{fill: '#ffffff'}} />
                </IconButton>
                <IconButton sx={{'&:hover': {backgroundColor: 'inherit'}, cursor: 'pointer', width: 'auto'}} onClick={handleFullscreen} disableRipple >
                    <FullscreenIcon htmlColor={'#ffffff'} sx={{fill: '#ffffff'}} />
                </IconButton>
            </Box>
            <Box>
                <IconButton
                    sx={{'&:hover':{backgroundColor: 'inherit'}, width: 'auto', cursor: 'pointer',}}
                    onClick={(e)=>{
                    e.preventDefault();
                    e.stopPropagation();
                    setMuted(prevState => !prevState);
                }} disableRipple>
                    {muted ? <VolumeOffIcon  sx={{fill: '#ffffff'}} /> : <VolumeUpIcon sx={{fill: '#ffffff'}} />}
                </IconButton>
            </Box>
        </Box>
    </div>
}

NativePlayer.defaultProps = {
    autoplay: false,
    muted: false,
    isLandscape: false,
    active: false,
    handleCloseModal: ()=>{}
}

export default NativePlayer;